/* eslint-disable max-len */
import React from 'react';
import { contentTypes } from '../../constants';

export const SERVICE_VIEWS = {
    ServiceList: 'ServiceList',
    ServiceEditor: 'ServiceEditor',
    ServicePreview: 'ServicePreview',
    LoadingSavingService: 'LoadingSavingService',
    LoadingServiceEditor: 'LoadingServiceEditor',
    LoadingServiceConfiguration: 'LoadingServiceConfiguration',
    LoadingPreview: 'LoadingPreview',
    LoadingInstallingService: 'LoadingInstallingService',
    ServiceConfiguration: 'ServiceConfiguration',
    NewServiceSelect: 'NewServiceSelect',
    NewFlowService: 'NewFlowService',
    NewIntegrationService: 'NewIntegrationService',
    NewCustomService: 'NewCustomService',
    ServiceDelete: 'ServiceDelete',
    LoadingDeletingService: 'LoadingDeletingService',
};

export const SUPPORTED_SERVICES = [
    {
        name: 'Dell Boomi Integration Service',
        view: SERVICE_VIEWS.NewIntegrationService,
    },
    {
        name: 'Custom Service',
        view: SERVICE_VIEWS.NewCustomService,
    },
    {
        name: 'Amazon Lex',
        techPreview: false,
        url: 'https://services.manywho.com/api/aws/lex/1',
        description: (
            <p>
                Integrate your flows with Amazon Lex, to build conversational bots for example.
            </p>
        ),
        configuration: [
            { name: 'Region', contentType: contentTypes.string, required: true, default: null, description: `The AWS region to use when communicating with Lex (available regions can be found ${<a href="https://docs.aws.amazon.com/general/latest/gr/rande.html#lex_region">here</a>})` },
            { name: 'Access Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM access key, with the appropriate permissions' },
            { name: 'Secret Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM secret key, with the appropriate permissions' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Amazon Polly',
        techPreview: true,
        url: 'https://services-staging.manywho.com/api/aws/polly/1',
        description: (
            <p>
                Integrate your flows with AWS Polly, to use text-to-speech functionality.
            </p>
        ),
        configuration: [
            { name: 'Region', contentType: contentTypes.string, required: true, default: null, description: `The AWS region to use when communicating with Polly (available regions can be found ${<a href="https://docs.aws.amazon.com/general/latest/gr/rande.html#lex_region">here</a>})` },
            { name: 'Access Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM access key, with the appropriate permissions' },
            { name: 'Secret Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM secret key, with the appropriate permissions' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Amazon Rekognition',
        techPreview: true,
        url: 'https://services-staging.manywho.com/api/aws/rekognition/1',
        description: (
            <p>
                Integrate your flows with AWS Rekognition, for image analysis and recognition.
            </p>
        ),
        configuration: [
            { name: 'Region', contentType: contentTypes.string, required: true, default: null, description: `The AWS region to use when communicating with Rekognition (available regions can be found ${<a href="https://docs.aws.amazon.com/general/latest/gr/rande.html">here</a>})` },
            { name: 'Bucket Region', contentType: contentTypes.string, required: true, default: null, description: `The AWS S3 region for the bucket used to temporarily store synthesized speech (available regions can be found ${<a href="https://docs.aws.amazon.com/general/latest/gr/rande.html">here</a>})` },
            { name: 'Bucket Name', contentType: contentTypes.string, required: true, default: null, description: 'The AWS S3 bucket used to temporarily store synthesized speech' },
            { name: 'Access Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM access key, with the appropriate permissions' },
            { name: 'Secret Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM secret key, with the appropriate permissions' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Amazon S3',
        techPreview: false,
        url: 'https://services.manywho.com/api/aws/s3/1',
        description: (
            <p>
                Allows your flows to upload, list and delete files in Amazon S3.
            </p>
        ),
        configuration: [
            { name: 'Region', contentType: contentTypes.string, required: true, default: null, description: `The AWS region containing your S3 bucket (available regions can be found ${<a href="https://docs.aws.amazon.com/general/latest/gr/rande.html">here</a>})` },
            { name: 'Access Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM access key, with the appropriate permissions' },
            { name: 'Secret Key', contentType: contentTypes.string, required: true, default: null, description: 'An IAM secret key, with the appropriate permissions' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Azure AD',
        techPreview: false,
        url: 'https://services.manywho.com/api/azure/1',
        description: (
            <p>
                Protect your flows using Azure Active Directory authentication.
            </p>
        ),
        configuration: [
            { name: 'Username', contentType: contentTypes.string, required: false, default: null, description: 'The Username for a user in your Azure AD organization. If set with a Password, the users and groups listed by Flow will be only the users and groups that the configured user is able to read.' },
            { name: 'Password', contentType: contentTypes.password, required: false, default: null, description: 'The Password for a user in your Azure AD organization. If set with a Username, the users and groups listed by Flow will be only the users and groups that the configured user is able to read.' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Box',
        techPreview: false,
        url: 'https://services.manywho.com/api/box/3',
        description: (
            <p>
                Integrate your flows with Box.
            </p>
        ),
        configuration: [
            { name: 'Enterprise Id', contentType: contentTypes.string, required: false, default: null, description: `You need to get this value from the Box Admin Console in section ${<a href="https://app.box.com/master/settings">Business Settings</a>}.` },
            { name: 'Verification Token', contentType: contentTypes.string, required: false, default: null, description: `You can run the ${<a href="https://flow.boomi.com/2d925770-d411-42b9-9ea3-7ce669397d82/play/default?flow-id=2d29b47c-6ded-4564-9571-da5203e83141&flow-version-id=752f11ad-6f6f-41f3-9800-bbfb70ec4606">verification token flow</a>} to get this code.` },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Email',
        techPreview: false,
        url: 'https://services.manywho.com/api/email/1',
        description: (
            <p>
                Send emails from your flows, including email-based decisions.
            </p>
        ),
        configuration: [
            { name: 'Password', contentType: contentTypes.string, required: false, default: null, description: 'Your password' },
            { name: 'Port', contentType: contentTypes.number, required: true, default: null, description: 'e.g. 587' },
            { name: 'Host', contentType: contentTypes.string, required: true, default: null, description: 'host e.g. smtp.gmail.com' },
            { name: 'Transport', contentType: contentTypes.string, required: true, default: null, description: 'valid values are: plain, ssl and tls' },
            { name: 'Username', contentType: contentTypes.string, required: false, default: null, description: 'username e.g. example@yourdomain.com' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Hub',
        techPreview: true,
        url: 'https://services-staging.manywho.com/api/mdh/1',
        description: (
            <p>
                Connect flows to a Boomi Master Data Hub Repository.
            </p>
        ),
        configuration: [
            { name: 'Hub Hostname', contentType: contentTypes.string, required: true, default: null, description: 'The host name or IP address of the repository' },
            { name: 'Hub Token', contentType: contentTypes.password, required: true, default: null, description: 'The authentication token for the account’s interactions with the repository. A repository’s authentication token is automatically generated when the repository is created.' },
            { name: 'Hub Username', contentType: contentTypes.string, required: true, default: null, description: 'The username for the account’s interactions with the repository. The prefix is the account ID.' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Okta',
        techPreview: false,
        url: 'https://services.manywho.com/api/okta/1',
        description: (
            <p>
                Protect your flows with Okta authentication.
            </p>
        ),
        configuration: [
            { name: 'Client ID', contentType: contentTypes.string, required: true, default: null, description: 'You get this value after you create a new app' },
            { name: 'Client Secret', contentType: contentTypes.password, required: true, default: null, description: 'You get this value after you create a new app' },
            { name: 'API Key', contentType: contentTypes.password, required: false, default: null, description: `You create this value when ${<a href="https://developer.okta.com/docs/api/getting_started/getting_a_token">creating an api token</a>}` },
            { name: 'IdP ID', contentType: contentTypes.string, required: false, default: null, description: 'From your IdP you will need to copy the value of the parameter idp from the Authorize URL' },
            { name: 'Org URL', contentType: contentTypes.string, required: true, default: null, description: `Find Org URL in your ${<a href="https://developer.okta.com/docs/api/getting_started/finding_your_domain">Okta dashboard</a>}` },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Onelogin',
        techPreview: true,
        url: 'https://services-staging.manywho.com/api/onelogin/1',
        description: (
            <p>
                Protect your flows with OneLogin authentication.
            </p>
        ),
        configuration: [
            { name: 'OpenId Client Secret', contentType: contentTypes.string, required: true, default: null, description: `This value is generated when creating the ${<a href="https://developers.onelogin.com/openid-connect/connect-to-onelogin">OpenId Connect app</a>}` },
            { name: 'Client ID (Api Access)', contentType: contentTypes.password, required: true, default: null, description: `This value is generated when ${<a href="https://developers.onelogin.com/api-docs/1/getting-started/working-with-api-credentials">creating the API Credentials</a>}` },
            { name: 'Region (Api Access)', contentType: contentTypes.string, required: true, default: null, description: 'e.g. us or eu' },
            { name: 'OpenId Client ID', contentType: contentTypes.string, required: true, default: null, description: `This value is generated when creating the ${<a href="https://developers.onelogin.com/openid-connect/connect-to-onelogin">OpenId Connect app</a>}` },
            { name: 'OpenId Client Region', contentType: contentTypes.string, required: true, default: null, description: 'This value is the one that is just before onelogin.com e.g. https://example-region-eu.onelogin.com in this case is example-region-eu' },
            { name: 'Client Secret (Api Access)', contentType: contentTypes.password, required: true, default: null, description: `This value is generated when ${<a href="https://developers.onelogin.com/api-docs/1/getting-started/working-with-api-credentials">creating the API Credentials</a>}` },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'PDF',
        techPreview: false,
        url: 'https://services.manywho.com/api/pdf/1',
        description: (
            <p>
                Allows your flows to work with PDF documents and forms.
            </p>
        ),
        configuration: [
            { name: 'PDF Form URL', contentType: contentTypes.string, required: false, default: null, description: 'The value that you can set here is a public URL where you can download the PDF Form that will be read it and then transformed in a “Form Type”.' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Runtime',
        techPreview: false,
        url: 'https://flow.manywho.com/plugins/manywho/api/run/1',
        description: (
            <p>
                Perform state-based actions in your flows, such as launching a parallel flow.
            </p>
        ),
        configuration: [
            { name: 'AuthenticationToken', contentType: contentTypes.string, required: false, default: null, description: '' },
            { name: 'StateId', contentType: contentTypes.string, required: false, default: null, description: '' },
            { name: 'StreamId', contentType: contentTypes.string, required: false, default: null, description: '' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Salesforce',
        techPreview: false,
        url: 'https://salesforce.manywho.com/plugins/api/salesforce/1',
        description: (
            <p>
                Integrate your flows with Salesforce, including authentication.
            </p>
        ),
        configuration: [
            { name: 'AuthenticationUrl', contentType: contentTypes.string, required: true, default: null, description: 'For developer edition and production accounts, this is: https://login.salesforce.com For sandboxes, this may be https://text.salesforce.com If you’re using your own domain, this would be the full URL of your own domain.' },
            { name: 'Username', contentType: contentTypes.string, required: true, default: null, description: 'Your username or the username of an API user account. This is needed for design time access.' },
            { name: 'Password', contentType: contentTypes.password, required: true, default: null, description: 'Your password or the password of an API user account. This is needed for design time access.' },
            { name: 'ChatterBaseUrl', contentType: contentTypes.string, required: true, default: null, description: 'This is the address of your Salesforce instance captured in the instructions above e.g. https://na30.salesforce.com' },
            { name: 'AdminEmail', contentType: contentTypes.string, required: true, default: null, description: 'Likely your email address.' },
            { name: 'SecurityToken', contentType: contentTypes.string, required: false, default: null, description: 'Your security token or the security token of an API user account. This can be needed for design time access and when SuperUser Strategy is used.' },
            { name: 'Consumer Secret', contentType: contentTypes.password, required: false, default: null, description: 'If you wish to leverage OAuth2 authentication, this is the Consumer Secret value you captured in the instructions above.' },
            { name: 'Consumer Key', contentType: contentTypes.string, required: false, default: null, description: 'If you wish to leverage OAuth2 authentication, this is the Consumer Key value you captured in the instructions above.' },
            { name: 'Authentication Strategy', contentType: contentTypes.string, required: false, default: 'Standard', description: 'Indicated how the service will get the credentials to interact with Salesforce. Valid values are Standard (or not provided/blank), ActiveUser and SuperUser' },
            { name: 'Default From Email', contentType: contentTypes.string, required: false, default: null, description: 'If you wish to send notification emails from ManyWho, this is usually the username of a valid email account e.g. mydemo@gmail.com' },
            { name: 'Email Username', contentType: contentTypes.string, required: false, default: null, description: 'The email username of a valid email account e.g. mydemo@gmail.com' },
            { name: 'Email Password', contentType: contentTypes.password, required: false, default: null, description: 'The email password of a valid email account.' },
            { name: 'Email SMTP', contentType: contentTypes.string, required: false, default: null, description: 'The SMTP server details for a valid email host e.g. smtp.gmail.com' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Sharepoint',
        techPreview: false,
        url: 'https://services.manywho.com/api/sharepoint/1',
        description: (
            <p>
                Integrate your flows with SharePoint, including embedded authentication.
            </p>
        ),
        configuration: [
            { name: 'Username', contentType: contentTypes.string, required: true, default: null, description: 'Username in SharePoint.' },
            { name: 'Password', contentType: contentTypes.password, required: true, default: null, description: 'Password in SharePoint.' },
            { name: 'Host', contentType: contentTypes.string, required: true, default: null, description: 'Subdomain for your organization in Sharepoint.' },
            { name: 'Authentication Strategy', contentType: contentTypes.string, required: false, default: 'Oauth', description: 'with SuperUser the flow will use Username and Password saved in the Configurations values as credentials, else it will use Oauth' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'SQL',
        techPreview: false,
        url: 'https://services.manywho.com/api/sql/2',
        description: (
            <p>
                Load, save and delete data using an SQL database.
            </p>
        ),
        configuration: [
            { name: 'Username', contentType: contentTypes.string, required: true, default: null, description: 'Username for your database.' },
            { name: 'Host', contentType: contentTypes.string, required: true, default: null, description: 'Host of your database (e.g. localhost, mydatabase.example.us-east-1.rds.amazonaws.com, etc).' },
            { name: 'Port', contentType: contentTypes.number, required: true, default: null, description: 'Port of your database (e.g. 3306, 5432, etc).' },
            { name: 'Database Type', contentType: contentTypes.string, required: true, default: null, description: 'Type of database used (choices are: postgresql, mysql or sqlserver).' },
            { name: 'Database Name', contentType: contentTypes.string, required: true, default: null, description: 'Name of your database.' },
            { name: 'Database schema', contentType: contentTypes.string, required: true, default: null, description: 'Database schema.' },
            { name: 'Password', contentType: contentTypes.password, required: true, default: null, description: 'Password for your database.' },
            { name: 'No SSL', contentType: contentTypes.boolean, required: true, default: true, description: 'Select true when your connection is not using SSL (the default value will use a SSL connection).' },
            { name: 'Server Public Certificate', contentType: contentTypes.string, required: false, default: null, description: 'Certificate for server verification.' },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Timer',
        techPreview: false,
        url: 'https://services.manywho.com/api/timer/1',
        description: (
            <p>
                Add timed and scheduled actions to your flows, such as time-based escalations.
            </p>
        ),
        configuration: [],
        view: SERVICE_VIEWS.NewFlowService,
    },
    {
        name: 'Twilio',
        techPreview: false,
        url: 'https://services.manywho.com/api/twilio/2',
        description: (
            <p>
                Integrate your flows with Twilio, including using Flow as an IVR.
            </p>
        ),
        configuration: [
            { name: 'Account SID', contentType: contentTypes.string, required: true, default: null, description: `You can find your account SID on ${<a href="https://www.twilio.com/user/account">your dashboard</a>}.` },
            { name: 'Auth Token', contentType: contentTypes.password, required: true, default: null, description: `You can find your Auth Token on ${<a href="https://www.twilio.com/user/account">your dashboard</a>}.` },
        ],
        view: SERVICE_VIEWS.NewFlowService,
    },
];
